// components/EventSummary/Attendees.js

import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Typography from '../Typography';
import AttendeeItem from './AttendeeItem';
import Button from '../Button/Button';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import HelmetIcon from '../Icons/HelmetIcon';

const Attendees = ({
  attendees,
  attendeesCount,
  organizer,
  organizerVehicle,
  containerBorderRadius,
  containerBorderColor,
  onShowAllAttendees,
  isLoggedIn = false
}) => {
  const { t: tEventDetail } = useTranslation('eventDetail');
  const navigate = useNavigate();
  const location = useLocation();

  const handleViewAllClick = () => {
    if (isLoggedIn) {
      onShowAllAttendees();
    } else {
      navigate('/signin', { state: { returnTo: location.pathname } });
    }
  };

  // Filtrar asistentes para mostrar solo los que tienen estado 'attending'
  const filteredAttendees = attendees.filter(attendee => attendee.status === 'attending');

  // Limitar a 4 asistentes filtrados para mostrar
  const maxAttendeesToShow = 4;
  const attendeesToShow = filteredAttendees.slice(0, maxAttendeesToShow);
  const hasMoreAttendees = filteredAttendees.length > maxAttendeesToShow;

  return (
    <AttendeesContainer
      $containerBorderRadius={containerBorderRadius}
      $containerBorderColor={containerBorderColor}>
      <Heading>
        <HeadingLeft>
          <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" as='p'>
            {/* Asistentes  */}
            {tEventDetail('eventAttendees.title')}
          </Typography>
          <AttendeeCounter>
            <Typography as="p" $variant="body-2-semibold" color={theme.colors.brandMain}>
              {attendeesCount}
            </Typography>
          </AttendeeCounter>
        </HeadingLeft>
        {hasMoreAttendees && (
          <Button $variant="ghost" onClick={handleViewAllClick}>
            {tEventDetail('eventAttendees.viewAll')}
          </Button>
        )}
      </Heading>
      <AttendeesList>
        {organizer._id ? (
          <OrganizerLink to={`/user/${organizer._id}`}>
            <Organizer>
              <OrganizerImages>
                {organizer.userAvatar ? (
                  <OrganizerAvatar
                    src={organizer.userAvatar}
                    alt={tEventDetail('eventDetailHeader.organizer')}
                  />
                ) : (
                  <AvatarPlaceholder>
                    <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                  </AvatarPlaceholder>
                )}
                {organizerVehicle && organizerVehicle.image && (
                  <OrganizerVehicle
                    src={organizerVehicle.image}
                    alt={tEventDetail('eventDetailHeader.organizerVehicle')}
                  />
                )}
              </OrganizerImages>
              <OrganizerInfo>
                <Typography
                  $variant="body-3-semibold"
                  style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    width: '100%',
                    textAlign: 'center'
                  }}>
                  {organizer.name} {organizer.lastName}
                </Typography>
                {organizerVehicle && organizerVehicle.image && (
                  <Typography
                    $variant="caption-medium"
                    color={theme.colors.defaultWeak}
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      width: '100%',
                      textAlign: 'center'
                    }}>
                    {organizerVehicle.nickname || `${organizerVehicle.brand} ${organizerVehicle.model}`}
                  </Typography>
                )}
              </OrganizerInfo>
              <OrganizerBadge>
                <OrganizerBadgeIcon src="/icons/organizer-helmet.svg" alt="Organizer badge" />
                <Typography $variant="caption-medium" color={theme.colors.brandMain}>
                  {tEventDetail('eventDetailHeader.organizer')}
                </Typography>
              </OrganizerBadge>
            </Organizer>
          </OrganizerLink>
        ) : (
          <Organizer>
            <OrganizerImages>
              <AvatarPlaceholder>
                <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
              </AvatarPlaceholder>
            </OrganizerImages>
            <OrganizerInfo>
              <Typography
                $variant="body-3-semibold"
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  width: '100%',
                  textAlign: 'center'
                }}>
                Organizador no disponible
              </Typography>
            </OrganizerInfo>
            <OrganizerBadge>
              <OrganizerBadgeIcon src="/icons/organizer-helmet.svg" alt="Organizer badge" />
              <Typography $variant="caption-medium" color={theme.colors.brandMain}>
                {tEventDetail('eventDetailHeader.organizer')}
              </Typography>
            </OrganizerBadge>
          </Organizer>
        )}
        {attendeesToShow.map((attendee) => (
          <AttendeeItem key={attendee._id} attendee={attendee} />
        ))}
        {hasMoreAttendees && (
          <ViewMoreCard onClick={handleViewAllClick}>
            <OrganizerImages>
              <AvatarPlaceholder>
                <HelmetIcon width={32} height={32} fill={theme.colors.defaultSubtle} />
              </AvatarPlaceholder>
            </OrganizerImages>
            <Info>
              <Typography $variant="body-3-semibold" color={theme.colors.defaultStrong}>
                +{filteredAttendees.length - maxAttendeesToShow} más
              </Typography>
              <Typography $variant="caption-medium" color={theme.colors.defaultWeak}>{tEventDetail('eventAttendees.viewAll')}</Typography>
            </Info>
          </ViewMoreCard>
        )}
      </AttendeesList>
    </AttendeesContainer>
  );
};

export default Attendees;

// Styled Components
const AttendeesContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: ${({ theme, $containerBorderColor }) => $containerBorderColor === 'transparent' ? 'none' : `1px solid ${$containerBorderColor || theme.border.defaultSubtle}`};
  border-radius: ${({ theme, $containerBorderRadius }) => $containerBorderRadius || theme.radius.sm};
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-bottom: 0px;
`;

const HeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const AttendeeCounter = styled.span`
  padding: ${({ theme }) => theme.sizing.xxs} ${({ theme }) => theme.sizing.xs};;
  background-color: ${({ theme }) => theme.fill.brandAlphaMain16};
  border-radius: ${({ theme }) => theme.radius.xxl};
  min-width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AttendeesList = styled.div`
  gap: ${({ theme }) => theme.sizing.xs};
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: ${({ theme }) => theme.sizing.sm};
  list-style: none;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    // background-color: purple;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    // background-color: blue;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    // background-color: red;
  }
`;

const OrganizerLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const Organizer = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.sizing.xs} 0px 0px 0px;
  flex-direction: column;
  justify-content: flex-start;    
  align-items: center;
  align-self: flex-start;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  overflow: hidden;
  cursor: pointer;
`;

const ViewMoreCard = styled(Organizer)`
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const Info = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OrganizerImages = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px ${({ theme }) => theme.sizing.xs};
`;

const OrganizerInfo = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;
`;

const OrganizerBadge = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.sizing.xxs};
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
  align-self: stretch;
  background: ${({ theme }) => theme.fill.brandAlphaMain16};

  .OrganizerBadgeIcon {
    height: ${({ theme }) => theme.sizing.sm};
  }
`;

const OrganizerBadgeIcon = styled.img`
  height: ${({ theme }) => theme.sizing.sm};
`;

const OrganizerAvatar = styled.img`
  width: ${({ theme }) => theme.sizing.xl};
  height: ${({ theme }) => theme.sizing.xl};
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  // box-shadow: 2px 0px 4px 0px rgba(0, 0, 0, 0.16);
  z-index: 2;
`;

const OrganizerVehicle = styled.img`
  width: ${({ theme }) => theme.sizing.lg};
  height: ${({ theme }) => theme.sizing.lg};
  border-radius: ${({ theme }) => theme.radius.xl};
  z-index: 1;
  margin-left: -${({ theme }) => theme.sizing.xxs};
`;

const AvatarPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.sizing.xl};
  height: ${({ theme }) => theme.sizing.xl};
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  z-index: 2;
`;